import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { leaveApi } from "../../services/leaveApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_FILTERS = ["ALL", "ACTION_REQUIRED", "DEADLINE_APPROACHING", "OVERDUE", "ON_TRACK", "COMPLETED", "REVIEW_REQUIRED", "WAIVED_BY_CORRECTION", "CALCULATION_PENDING"];

export default function LeaveMandatoryUsePage() {
  const { t } = useLocalization();
  const { canViewLeave, canManageLeave } = usePermissions();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [waiveTarget, setWaiveTarget] = useState(null);

  const load = useCallback(() => {
    leaveApi.mandatoryUseRollup(statusFilter === "ALL" ? undefined : statusFilter)
      .then((r) => setRows(r.data)).catch(() => {});
  }, [statusFilter]);

  useEffect(() => { if (canViewLeave) load(); }, [canViewLeave, load]);

  if (!canViewLeave) return <p className="text-sm text-gray-500" data-testid="mandatory-use-access-denied">{t("common.accessDenied")}</p>;

  const urgentCount = rows.filter((r) => ["ACTION_REQUIRED", "DEADLINE_APPROACHING", "OVERDUE"].includes(r.status)).length;

  return (
    <div className="space-y-5" data-testid="leave-mandatory-use-page">
      <PageHelpBanner pageKey="leave-mandatory-use" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtLeaveDetail.mandatoryUseHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtLeaveDetail.mandatoryUseTitle")}</h2>
        <p className="text-sm text-gray-500">
          {t("mgmtLeaveDetail.mandatoryUseSubtitle")}
          {urgentCount > 0 && <span className="ml-2 font-semibold text-red-600" data-testid="mandatory-use-urgent-count">{urgentCount} {t("mgmtLeaveDetail.requireAttention")}</span>}
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            data-testid={`mandatory-use-filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${statusFilter === s ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 bg-white hover:bg-gray-50"}`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.window")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.required")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.used")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.planned")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.remaining")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.grant_id} className="border-t border-gray-100" data-testid={`mandatory-use-row-${r.grant_id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{r.employee_name} <span className="text-gray-500 font-normal">({r.employee_code})</span></td>
                <td className="px-4 py-2 text-gray-600">{r.monitoring_start} → {r.monitoring_end}</td>
                <td className="px-4 py-2 text-gray-600">{r.required_use_total}</td>
                <td className="px-4 py-2 text-gray-600">{r.used_amount}</td>
                <td className="px-4 py-2 text-gray-600">{r.planned_approved_leave}</td>
                <td className="px-4 py-2 font-semibold text-gray-800">{r.remaining_mandatory_amount}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2">
                  {canManageLeave && !r.waived && r.status !== "COMPLETED" && (
                    <button data-testid={`mandatory-use-waive-btn-${r.grant_id}`} onClick={() => setWaiveTarget(r)} className="text-xs font-semibold text-red-600 hover:underline">{t("mgmtLeaveDetail.waiveEllipsis")}</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500" data-testid="no-mandatory-use-rows">{t("mgmtLeaveDetail.noGrantsMatchFilter")}</td></tr>}
          </tbody>
        </table>
      </div>

      {waiveTarget && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setWaiveTarget(null)} title={t("mgmtLeaveDetail.waiveWindowTitle")}
          contextItems={[
            { label: t("mgmtPayroll.colEmployee"), value: `${waiveTarget.employee_name} (${waiveTarget.employee_code})` },
            { label: t("mgmtLeaveDetail.window"), value: `${waiveTarget.monitoring_start} → ${waiveTarget.monitoring_end}` },
            { label: t("mgmtLeaveDetail.requiredTotal"), value: waiveTarget.required_use_total },
            { label: t("mgmtLeaveDetail.usedSoFar"), value: waiveTarget.used_amount },
            { label: t("mgmtLeaveDetail.remaining"), value: waiveTarget.remaining_mandatory_amount },
            { label: t("mgmtLeaveDetail.currentStatus"), value: waiveTarget.status },
          ]}
          reasonLabel={t("mgmtLeaveDetail.waiverReason")} danger confirmLabel={t("mgmtLeaveDetail.waiveWindow")}
          onConfirm={async (reason) => { await leaveApi.waiveMandatoryUse(waiveTarget.grant_id, reason); toast.success(t("mgmtLeaveDetail.windowWaived")); load(); }}
          testId="waive-mandatory-use-dialog"
        />
      )}
    </div>
  );
}

